import { inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { concatLatestFrom } from '@ngrx/operators';
import { Store, createSelector } from "@ngrx/store";
import { filter, map } from "rxjs/operators";

import { convertDollarsToCents } from "../../lib.ts/currency.util";
import { generateUUID } from "../../utilities/state.util";
import { selectJob } from "../job-tool.reducer";
import { WorkOrdersActions } from "../job-workorders/job-workorders.actions";
import { jobWorkordersFeature } from "../job-workorders/job-workorders.feature";
import { EditableDiscount } from "../job-workorders/job-workorders.state";

import { AddDiscountsModalActions } from "./add-discounts-modal.actions";
import { addDiscountsModalFeature } from "./add-discounts-modal.feature";

export const openMutateDiscountDialog = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(AddDiscountsModalActions.newSingleUseButtonClicked),
      concatLatestFrom(() => [
        store.select(selectJob),
        store.select(addDiscountsModalFeature.selectAddDiscountsModal),
      ]),
      filter(([_, job]) => Boolean(job?.code)),
      map(([ _, { code: jobCode }, { eventIds } ]) => AddDiscountsModalActions.mutateDiscountDialogOpened({
        jobCode,
        eventIds,
      })));
  }, { functional:true, dispatch: true });

export const addDiscount = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(AddDiscountsModalActions.applyChangesButtonClicked),
      concatLatestFrom(({ amountTouched }) => store.select(createSelector(
        addDiscountsModalFeature.selectAddDiscountsModal,
        jobWorkordersFeature.selectAvailableDiscounts,
        ({ amount, discountId, eventIds }, availableDiscounts) => ({
          customAmount: amountTouched ? amount : undefined,
          selectedDiscount: availableDiscounts.find((d) => d.id === discountId),
          eventIds,
        }),
      ))),
      map(([ _action, { customAmount, selectedDiscount, eventIds } ]): {
        discount: EditableDiscount;
        eventIds: string[];
      } => ({
        eventIds,
        discount: {
          appliedId: generateUUID(),
          name: selectedDiscount.name,
          code: selectedDiscount.code,
          discountId: selectedDiscount.id,
          // The modal's amount input is displayed in dollars (spinner min/max are
          // converted from the discount's cents customAmountRange), but the backend
          // stores custom amounts in cents — convert before dispatching, matching
          // edit-custom-amount's getCustomAmount(). Percentage discounts are unitless
          // and pass through unchanged.
          customAmount: selectedDiscount.discountType === 'amount'
            ? convertDollarsToCents(customAmount, true)
            : customAmount,
          new: true,
          removed: false,
        },
      })),
      map((payload) => WorkOrdersActions.discountAdded(payload)),
    );
  }, { functional:true, dispatch: true });
